import React, { useEffect } from "react";
import { BundleInterface } from "../../../../store/mine_craft/bundlesSlice";

import { Button, Divider, Form, Input, Select, Switch, Upload, message } from "antd";
import type { RcFile } from "antd/es/upload";
import { UploadOutlined } from "@ant-design/icons";
import FileDropzone from "../../../../componets/FileDropzone";

interface propsInterface {
  params?: BundleInterface | undefined;
  onFinish: Function;
}

// Mirrors the platform enum on Bundle (`ios: 1, android: 2, web: 3`).
// Keep these strings in sync with the enum keys on the backend.
const PLATFORM_OPTIONS = [
  { value: 'ios',     label: 'iOS' },
  { value: 'android', label: 'Android' },
  { value: 'web',     label: 'Web' }
];

const _Form = ({ params, onFinish }: propsInterface) => {
  const [form] = Form.useForm();

  useEffect(() => {
    form.resetFields();
  }, [params, form]);

  // Watch the live form value so the attestation section reacts to
  // platform changes the operator makes inside the drawer (instead of
  // staying frozen on the bundle's persisted platform).
  const platform = Form.useWatch('platform', form) ?? params?.platform;

  // Attestation credentials are platform-specific: iOS uses App Attest
  // (apple_team_id), android uses Play Integrity (firebase_*). Hide the
  // irrelevant set entirely so operators can't accidentally populate
  // fields that the verifier won't read. When the platform is `web`
  // we show none — there's no attestation to configure.
  const isIos     = platform === 'ios';
  const isAndroid = platform === 'android';
  const showAttestation = isIos || isAndroid;

  const importServiceAccountJson = (file: RcFile) => {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result as string);

        if (!json.project_id && !json.client_email && !json.private_key) {
          message.error("No service account fields found in the selected file");
          return;
        }

        form.setFieldsValue({
          firebase_project_id: json.project_id,
          firebase_client_email: json.client_email,
          // keep newlines as the literal "\n" sequence rather than real line breaks
          firebase_private_key: json.private_key?.replace(/\r?\n/g, "\\n"),
        });

        message.success("Service account fields imported from JSON");
      } catch {
        message.error("Could not parse the selected file as JSON");
      }
    };

    reader.readAsText(file);

    return false; // prevent antd from uploading the file
  };

  return (
    <Form
      form={form}
      onFinish={values => onFinish(values)}
      layout="vertical"
      className='v-form'
      initialValues={params}
    >
      <FileDropzone name='push_cert' label='Push Cert' />

      <Form.Item name='name' label="Name">
        <Input disabled />
      </Form.Item>

      <Form.Item
        name='platform'
        label='Platform'
        tooltip='Drives which attestation flow runs and which credential set is required.'
      >
        <Select options={PLATFORM_OPTIONS} placeholder='Select platform' />
      </Form.Item>

      <Form.Item label="Notification" name='notification' valuePropName='checked'>
        <Switch
          checkedChildren='enabled'
          unCheckedChildren='disabled'
        />
      </Form.Item>

      <Form.Item label="Environment" name='prod' valuePropName='checked'>
        <Switch
          checkedChildren='production'
          unCheckedChildren='development'
        />
      </Form.Item>

      {showAttestation && (
        <h6 className="v-content-label o-sm">
          Attestation
        </h6>
      )}

      {isIos && (
        <>
          <Form.Item
            name='apple_team_id'
            label='Apple Team ID'
            tooltip='10-char Apple Developer Team ID (e.g. ABCDE12345). Combined with the bundle name to derive the App Attest rpIdHash. NOT the same as the App Store Connect Issuer ID below.'
          >
            <Input placeholder='ABCDE12345' />
          </Form.Item>

          <h6 className='v-content-label o-sm'>
            In-App Purchases (StoreKit2)
          </h6>

          <Form.Item
            name='app_store_connect_api_key_id'
            label='App Store Connect API Key ID'
            tooltip='Key ID shown next to the API key in App Store Connect → Users & Access → Keys.'
          >
            <Input placeholder='ABCD1EF2GH' />
          </Form.Item>

          <Form.Item
            name='app_store_connect_api_issuer_id'
            label='App Store Connect API Issuer ID'
            tooltip='UUID shown above the API keys list in App Store Connect → Users & Access → Keys. Used as the JWT `iss` claim. NOT the same as the Apple Team ID above.'
          >
            <Input placeholder='57246542-96fe-1a63-e053-0824d011072a' />
          </Form.Item>

          <FileDropzone
            name='app_store_connect_api_private_key'
            label='App Store Connect API Private Key (.p8)'
          />
        </>
      )}

      {isAndroid && (
        <>
          <Upload
            accept=".json,application/json"
            showUploadList={false}
            maxCount={1}
            beforeUpload={importServiceAccountJson}
          >
            <Button className="v-mb-10" icon={<UploadOutlined />}>
              Import from service account JSON
            </Button>
          </Upload>

          <Form.Item
            name='firebase_project_id'
            label='Firebase Project ID'
            tooltip='Maps to `project_id` in the service-account JSON.'
          >
            <Input placeholder='my-app-prod' />
          </Form.Item>

          <Form.Item
            name='firebase_client_email'
            label='Firebase Client Email'
            tooltip='Service-account email (`*@*.iam.gserviceaccount.com`).'
          >
            <Input placeholder='svc@my-app-prod.iam.gserviceaccount.com' />
          </Form.Item>

          <Form.Item
            name='firebase_private_key'
            label='Firebase Private Key'
            tooltip='PEM block from the service-account JSON. Paste the full -----BEGIN/END----- block including newlines.'
          >
            <Input.TextArea
              rows={6}
              autoSize={{ minRows: 6, maxRows: 16 }}
              placeholder={'-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----\n'}
            />
          </Form.Item>
        </>
      )}

      <Form.Item>
        <Button className="v-btn v-btn-primary" type="primary" htmlType="submit">
          Submit
        </Button>
      </Form.Item>
    </Form>
  )
}

export default _Form;
